import { playAlertRing } from './sound';
import { encryptChatToken } from './chat-encryption';

let isAudioUnlocked = false;

const TAB_INSTANCE_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `tab_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const STORAGE_KEY_ACTIVE_TAB = 'zeric_active_tab_state';
let lastUserActivityTime = typeof Date !== 'undefined' ? Date.now() : 0;
let tabTrackingInitialized = false;
let heartbeatInterval: any = null;

/**
 * Checks if the current document is visible and actively focused or interacted with.
 */
export function isCurrentTabActive(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  // Must be visible (not minimized and not an inactive background tab in Chrome)
  if (document.visibilityState !== 'visible') {
    return false;
  }

  // Document must have focus or recent user interaction (within the last 5 seconds)
  const hasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  const hasRecentActivity = Date.now() - lastUserActivityTime < 5000;

  return hasFocus || hasRecentActivity;
}

/**
 * Checks if https://zeric.link (or the current application host) is opened and is the active tab.
 * Checks both the current tab AND cross-tab state in localStorage (so background tabs know if
 * another zeric.link tab is currently active).
 */
export function isZericActiveTab(): boolean {
  if (typeof window === 'undefined') return false;

  // 1. Check if the current tab itself is active
  if (isCurrentTabActive()) {
    return true;
  }

  // 2. Check if another zeric.link tab has an active heartbeat in localStorage
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACTIVE_TAB);
    if (raw) {
      const data = JSON.parse(raw);
      // If a heartbeat was refreshed within the last 2500ms, another tab is actively in use
      if (data && data.timestamp && Date.now() - Number(data.timestamp) < 2500) {
        return true;
      }
    }
  } catch {}

  return false;
}

/**
 * Query Service Worker to see if any window client for zeric.link is currently visible and focused.
 */
export async function checkServiceWorkerActiveTab(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;

  const controller = navigator.serviceWorker?.controller;
  if (!controller) return false;

  return new Promise<boolean>((resolve) => {
    try {
      const channel = new MessageChannel();
      const timeout = setTimeout(() => {
        resolve(false);
      }, 150);

      channel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        resolve(Boolean(event.data?.hasActiveTab));
      };

      controller.postMessage({ type: 'CHECK_ACTIVE_TAB' }, [channel.port2]);
    } catch {
      resolve(false);
    }
  });
}

function updateTabHeartbeat() {
  if (typeof window === 'undefined') return;
  try {
    if (isCurrentTabActive()) {
      localStorage.setItem(
        STORAGE_KEY_ACTIVE_TAB,
        JSON.stringify({
          tabId: TAB_INSTANCE_ID,
          url: window.location.href,
          timestamp: Date.now(),
        })
      );
    }
  } catch {}
}

function clearTabHeartbeatIfOwner() {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_ACTIVE_TAB);
    if (raw) {
      const data = JSON.parse(raw);
      if (data && data.tabId === TAB_INSTANCE_ID) {
        localStorage.removeItem(STORAGE_KEY_ACTIVE_TAB);
      }
    }
  } catch {}
}

export function initActiveTabTracking() {
  if (typeof window === 'undefined' || tabTrackingInitialized) return;
  tabTrackingInitialized = true;

  const recordActivity = () => {
    lastUserActivityTime = Date.now();
    updateTabHeartbeat();
  };

  window.addEventListener('mousemove', recordActivity, { passive: true });
  window.addEventListener('keydown', recordActivity, { passive: true });
  window.addEventListener('click', recordActivity, { passive: true });
  window.addEventListener('focus', recordActivity, { passive: true });

  window.addEventListener('blur', () => {
    clearTabHeartbeatIfOwner();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      recordActivity();
    } else {
      clearTabHeartbeatIfOwner();
    }
  });

  window.addEventListener('beforeunload', () => {
    clearTabHeartbeatIfOwner();
  });

  // Keep heartbeat alive while tab remains active
  if (heartbeatInterval) clearInterval(heartbeatInterval);
  heartbeatInterval = setInterval(() => {
    if (isCurrentTabActive()) {
      updateTabHeartbeat();
    }
  }, 1000);

  // Initial heartbeat if tab starts active
  if (isCurrentTabActive()) {
    updateTabHeartbeat();
  }
}

// Auto-initialize active tab tracking in client context
if (typeof window !== 'undefined') {
  initActiveTabTracking();
}

/**
 * Prime audio playback for Google Chrome and Safari to bypass autoplay policy restrictions.
 * Chrome blocks audio until a user interaction (click/touch/key) occurs on the document.
 */
export function initAudioUnlock() {
  if (typeof window === 'undefined' || isAudioUnlocked) return;

  initActiveTabTracking();

  const unlock = () => {
    try {
      const silentAudio = new Audio('/sounds/notification_1.mp3');
      silentAudio.volume = 0.01;
      silentAudio.play().then(() => {
        silentAudio.pause();
        isAudioUnlocked = true;
      }).catch(() => {});
    } catch {}

    window.removeEventListener('click', unlock);
    window.removeEventListener('touchstart', unlock);
    window.removeEventListener('keydown', unlock);
  };

  window.addEventListener('click', unlock, { once: true, passive: true });
  window.addEventListener('touchstart', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true, passive: true });
}

/**
 * Register Service Worker for Chrome Desktop and Mobile Notifications.
 */
export async function registerNotificationServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;

  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (err) {
    console.warn('[Notifications] Service Worker registration skipped:', err);
    return null;
  }
}

/**
 * Request notification permission from Google Chrome or any modern browser.
 * Registers SW, primes audio, and displays an instant test notification upon grant.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  initAudioUnlock();
  await registerNotificationServiceWorker();

  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      // Play a confirmation chime
      playAlertRing('sound1', true);

      // Show immediate confirmation notification (force: true to allow initial user-triggered verification)
      showDesktopNotification({
        title: 'Notifications Enabled',
        body: 'You will now receive instant alerts in Chrome for new WhatsApp messages.',
        icon: '/zeric-mark.svg',
        tag: 'welcome-notification',
        force: true,
      });
    }
    return permission;
  } catch (err) {
    console.error('[Notifications] Error requesting permission:', err);
    return 'denied';
  }
}

export interface IncomingNotificationOptions {
  senderName: string;
  messageText: string;
  avatarUrl?: string | null;
  remoteJid?: string;
  chatId?: number | string;
  instanceId?: number;
  url?: string;
  soundId?: string;
  isFirstMessage?: boolean;
}

/**
 * Displays a desktop notification in Google Chrome / browser.
 * Suppressed if https://zeric.link is opened and active tab (unless options.force is true).
 */
export async function showDesktopNotification(options: {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  url?: string;
  force?: boolean;
  onClick?: () => void;
}) {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  // If https://zeric.link is opened and is the active tab, do not send Chrome notification alert
  if (!options.force) {
    if (isZericActiveTab()) {
      return;
    }
    const isSwActive = await checkServiceWorkerActiveTab();
    if (isSwActive) {
      return;
    }
  }

  const title = options.title || 'New WhatsApp Message';
  const notificationOptions: NotificationOptions & { renotify?: boolean } = {
    body: options.body || 'You have received a new message.',
    icon: options.icon || '/zeric-mark.svg',
    badge: '/zeric-favicon.svg',
    tag: options.tag || 'whats-crm-message',
    renotify: true,
    data: {
      url: options.url || '/dashboard',
    },
  };

  // Try Service Worker notification first (Required for Android Chrome and best for Desktop Chrome)
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (reg && 'showNotification' in reg) {
        await reg.showNotification(title, notificationOptions);
        return;
      }
    } catch {}
  }

  // Fallback to standard window Notification constructor
  try {
    const n = new Notification(title, notificationOptions);
    n.onclick = () => {
      window.focus();
      if (options.onClick) {
        options.onClick();
      } else if (options.url) {
        window.location.href = options.url;
      }
      n.close();
    };
  } catch (err) {
    console.warn('[Notifications] Failed to display notification:', err);
  }
}

/**
 * High-level handler called whenever an incoming WhatsApp message is received.
 */
export function handleIncomingMessageAlert(payload: IncomingNotificationOptions) {
  if (typeof window === 'undefined') return;

  // Check if sender is muted
  if (payload.remoteJid) {
    try {
      const storedMuted = localStorage.getItem('mutedChats');
      if (storedMuted) {
        const mutedSet = new Set(JSON.parse(storedMuted));
        if (mutedSet.has(payload.remoteJid)) return;
      }
    } catch {}
  }

  // Check if user is currently focused and reading this exact chat
  const activeChatId = (window as any).__activeChatId;
  const isDocumentFocused = typeof document !== 'undefined' ? document.hasFocus() : false;
  const isViewingThisChat =
    isDocumentFocused &&
    payload.chatId &&
    activeChatId &&
    String(activeChatId) === String(payload.chatId);

  // Always play alert sound if not actively typing inside that conversation
  if (!isViewingThisChat) {
    playAlertRing(payload.soundId);
  }

  // If https://zeric.link is opened and active tab, do NOT send chrome notification alert!
  if (isZericActiveTab()) {
    return;
  }

  // If tab is in background, minimized, or viewing another tab/app, trigger desktop Chrome notification
  if ('Notification' in window && Notification.permission === 'granted') {
    const cleanJid = payload.remoteJid
      ? (payload.remoteJid.endsWith('@g.us') || payload.remoteJid.endsWith('@web.chat')
          ? payload.remoteJid
          : payload.remoteJid.split('@')[0])
      : '';

    const targetUrl = cleanJid
      ? `/dashboard/chat/${encryptChatToken(cleanJid, payload.instanceId)}`
      : '/dashboard';

    showDesktopNotification({
      title: payload.senderName || 'New WhatsApp Message',
      body: payload.messageText || 'Sent you a message',
      icon: payload.avatarUrl || '/zeric-mark.svg',
      tag: `chat-${payload.chatId || payload.remoteJid || 'msg'}`,
      url: targetUrl,
      onClick: () => {
        window.focus();
        if (window.location.pathname !== targetUrl) {
          window.location.href = targetUrl;
        }
      },
    });
  }
}
